import { robotModes } from '../utils/constants'
import { DiagnosticNotificationDto } from 'api/asyncapi'
import {
  MISSION_JOB_STATUSES,
  ROBOT_COMMANDS,
  ROBOT_MODES,
  ROBOT_NETWORK_STATUSES,
  ROBOT_STATUSES,
} from 'utils/constants'
import { IDiagnosticHistory, IDiagnosticNotification, IRobotInfo, ITelemetryPosition } from 'utils/interfaces'

import { DiagnosticLog, MainService, MissionHistory, MissionJob, ResolvedByEnum, RobotList, RobotRead } from './schema'

const normalizeRobot = (robotRaw: RobotRead | RobotList): IRobotInfo => {
  const activeMode = ('control_mode' in robotRaw ? robotRaw.control_mode : robotRaw.active_mode) ?? ROBOT_MODES.NOT_SET

  return {
    id: robotRaw.id,
    serialNumber: robotRaw.serial_number ?? 0,
    name: robotRaw.name || '',
    description: robotRaw.description || '',
    createdAt: robotRaw.created_at,
    updatedAt: robotRaw.updated_at,
    status: ROBOT_STATUSES.AVAILABLE,
    robotRaw,
    activeMode: activeMode as ROBOT_MODES,
    activeModeText: robotModes[activeMode as ROBOT_MODES],
    job: robotRaw.missions_jobs[0] && {
      id: robotRaw.missions_jobs[0].id,
      missionName: robotRaw.missions_jobs[0].mission_name,
      missionId: robotRaw.missions_jobs[0].mission_id,
      status: robotRaw.missions_jobs[0].status as MISSION_JOB_STATUSES,
      progress: robotRaw.missions_jobs[0].mission_status_last?.mission_progress?.overall_mission_feedback?.progress,
      overall: robotRaw.missions_jobs[0].mission_status_last?.mission_progress?.overall_mission_feedback?.overall,
    },
    telemetryPositionData: Array.isArray(robotRaw.telemetry_route)
      ? (robotRaw.telemetry_route as ITelemetryPosition[])
      : undefined,
    batteryPower: 'battery_capacity' in robotRaw ? robotRaw.battery_capacity ?? undefined : undefined,
    networkStatus: (robotRaw.network_status as ROBOT_NETWORK_STATUSES) || ROBOT_NETWORK_STATUSES.OFFLINE,
    speed: robotRaw.telemetry_last_speed ?? undefined,
    lastPosition: robotRaw.telemetry_last_point ?? undefined,
    lastOrientation: (robotRaw.telemetry_last_orientation as { x: number; y: number; z: number; w: number } | null) ?? undefined,
    maxCoverageArea: robotRaw.max_coverage_area ?? undefined,
  }
}

/**
 * Нормализация диагностического уведомления из wire-формата (AsyncAPI) во внутренний интерфейс.
 * Приводит опциональные поля схемы к обязательным значениям внутреннего IDiagnosticNotification.
 */
const normalizeDiagnosticNotification = (raw: DiagnosticNotificationDto): IDiagnosticNotification => {
  return {
    notification_id: raw.notification_id,
    robot_id: raw.robot_id,
    code: raw.code,
    message: raw.message ?? '',
    timeout: raw.timeout ?? 0,
    buttons: (raw.buttons ?? []).map(button => ({
      id: button.id,
      label: button.label,
    })),
  }
}

/**
 * Нормализация списка диагностических уведомлений, приходящих по WebSocket (diagnostic.notification).
 */
export const normalizeDiagnosticNotifications = (
  rawNotifications: DiagnosticNotificationDto[]
): IDiagnosticNotification[] => rawNotifications.map(normalizeDiagnosticNotification)

/**
 * Нормализация записи диагностической истории из wire-формата (schema) во внутренний интерфейс.
 */
export const normalizeDiagnosticHistory = (raw: DiagnosticLog): IDiagnosticHistory => ({
  id: raw.id,
  message: raw.message,
  action: raw.selected_button_label ?? 'None',
  timestamp: raw.created_at,
  resolvedBy: (raw.resolved_by ?? 0) as ResolvedByEnum,
})

export const RobotApi = {
  fetchRobots: async (): Promise<IRobotInfo[]> => {
    const robots = await MainService.mainRobotList()
    if (!robots.results) {
      return []
    }
    const normalizedRobots = robots.results.map(robotRaw => normalizeRobot(robotRaw))
    return normalizedRobots
  },
  createRobot: async (name: string, description: string, serialNumber: number): Promise<boolean> => {
    const robot = await MainService.mainRobotCreate({
      name,
      description,
      users_ids: [],
      serial_number: serialNumber,
    })
    if (robot) {
      return true
    }
    return false
  },
  patchRobot: async (id: number, name: string, description: string): Promise<boolean> => {
    const robot = await MainService.mainRobotPartialUpdate(id, {
      name,
      description,
    })
    if (robot) {
      return true
    }
    return false
  },
  fetchRobot: async (id: number): Promise<IRobotInfo> => {
    const robotRaw = await MainService.mainRobotRetrieve(id)
    const robot = normalizeRobot(robotRaw)
    return robot
  },
  deleteRobot: async (id: number): Promise<void> => {
    await MainService.mainRobotDestroy(id)
  },
  createJob: async (param: { missionId: number; robotId: number }): Promise<MissionJob> => {
    const mission = await MainService.mainMissionJobCreate({
      mission_id: param.missionId,
      robot_id: param.robotId,
    })
    return mission
  },
  uploadJobToRobot: async (jobId: number) => {
    await MainService.mainMissionJobStartCalculationCreate(jobId)
  },
  missionClearByRobotId: async (robotId: number) => {
    await MainService.mainMissionJobClearMissionByRobotCreate({ robot_id: robotId })
  },
  switchMode: async (params: { id: number; mode: ROBOT_MODES }) => {
    await MainService.mainRobotSwitchModeCreate(params.id, String(params.mode))
  },
  execCommand: async (params: { id: number; command: ROBOT_COMMANDS }) => {
    await MainService.mainRobotExecCommandCreate(String(params.command), params.id)
  },
  fetchRobotLastMissions: async (id: number): Promise<MissionHistory[]> => {
    const response = await MainService.mainMissionHistoryList(undefined, undefined, undefined, undefined, id)
    return response.results || []
  },
  fetchDiagnosticHistory: async (robotId: number): Promise<IDiagnosticHistory[]> => {
    const response = await MainService.mainDiagnosticLogList(undefined, undefined, robotId)
    return (response.results || []).map(normalizeDiagnosticHistory)
  },
}
